import React, { useState, useMemo, useEffect } from 'react';
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import graphql from 'src/utils/graphql';
import LoadingButton from 'src/components/LoadingButton';
import { useApp } from './appContext';

export default function EmergencyContactModal() {
	const { user, fetchUser } = useApp();
	const [dismissed, setDismissed] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [state, setState] = useState({
		emergencyContactName: '',
		emergencyContactMobile: ''
	});

	const oneTimeNotificationPending = useMemo(() => {
		if (!user || !user.tenant) return false;
		const key = user.tenant.oneTimeNotificationKey;
		const text = user.tenant.oneTimeNotification;
		if (!key || !text) return false;
		return user.lastSeenTenantOneTimeNotificationKey !== key;
	}, [user]);

	const shouldShow = useMemo(() => {
		if (!user || dismissed || oneTimeNotificationPending) return false;
		return !user.emergencyContactName || !user.emergencyContactMobile;
	}, [user, dismissed, oneTimeNotificationPending]);

	useEffect(() => {
		if (!user) return;
		setState({
			emergencyContactName: user.emergencyContactName || '',
			emergencyContactMobile: user.emergencyContactMobile || ''
		});
	}, [user]);

	if (!shouldShow) return null;

	const complete = !!state.emergencyContactName?.trim() && !!state.emergencyContactMobile?.trim();

	const handleChange = (e) => {
		setState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			setError(null);
			await graphql({
				query: `mutation ($input: PersonInput!) {
					savePerson(input: $input)
				}`,
				variables: {
					input: {
						id: user.id,
						emergencyContactName: state.emergencyContactName.trim(),
						emergencyContactMobile: state.emergencyContactMobile.trim()
					}
				}
			});
			await fetchUser();
		} catch (err) {
			console.error(err);
			setError(err.message || 'Failed to save emergency contact');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={true} toggle={() => setDismissed(true)} centered>
			<ModalHeader toggle={() => setDismissed(true)}>
				<i className="fa fa-phone-alt" style={{ marginRight: 8, color: '#667eea' }}></i>
				Emergency contact needed
			</ModalHeader>
			<ModalBody>
				<Alert color="info" className="mb-3">
					Please add an emergency contact name and number so we can reach someone if needed during your session.
				</Alert>
				{error && <Alert color="danger">{error}</Alert>}
				<FormGroup floating>
					<Input
						type="text"
						name="emergencyContactName"
						id="emergencyContactName"
						value={state.emergencyContactName}
						onChange={handleChange}
						placeholder="Emergency Contact Name"
					/>
					<Label for="emergencyContactName">Emergency Contact Name</Label>
				</FormGroup>
				<FormGroup floating>
					<Input
						type="text"
						name="emergencyContactMobile"
						id="emergencyContactMobile"
						value={state.emergencyContactMobile}
						onChange={handleChange}
						placeholder="Emergency Contact Number"
					/>
					<Label for="emergencyContactMobile">Emergency Contact Number</Label>
				</FormGroup>
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={() => setDismissed(true)} disabled={saving}>
					Later
				</Button>
				<LoadingButton loading={saving} disabled={!complete} color="primary" onClick={handleSave}>
					Save
				</LoadingButton>
			</ModalFooter>
		</Modal>
	);
}
